/**
 * Custom Stock Block v7.6 - Production Grade
 *
 * v7.6 Changes (2026-10-09, Astra residual):
 * ✓ The launch date reaches the script as "YYYY-MM-DDT00:00:00" (the Date
 *   metafield through Liquid's `date` filter). Without a zone every browser
 *   read that as its own local midnight, so at 2026-10-08T22:30Z a Los Angeles
 *   visitor still saw the launch state while Berlin and Tokyo saw sold out.
 *   launchDay() now reduces it to the bare warehouse date; pending means
 *   "before that day in Europe/Berlin", the same in every browser.
 *
 * v7.5 Changes (2026-10-08, Astra review of v1.0 Ship Line):
 * ✓ Every calendar decision runs on the warehouse wall clock
 *   (config.timezone, default Europe/Berlin) taken from the instant with
 *   Intl.DateTimeFormat#formatToParts. Dates are 'YYYY-MM-DD' strings, day
 *   math runs on UTC noon, holiday keys come from the warehouse date, the
 *   cutoff is a warehouse instant (DST-safe). The browser's own zone never
 *   enters: a visitor in Tokyo sees the same dispatch day as one in Bochum.
 *   Before, the holiday check used toISOString() of a browser-local date, so
 *   2026-12-24T06:00Z gave Dec 24 in Tokyo instead of Dec 28.
 * ✓ Restock and launch dates (date-only metafields) display without a zone
 *   shift and compare against the warehouse date.
 * ✓ stockblock_time without a zone (2026-10-09T09:18:00) means warehouse
 *   wall-clock time in every browser.
 *
 * v7.4 Changes (2026-10-08, v1.0 Ship Line):
 * ✓ Publishes the dispatch day it states and how it states it
 *   (block.dataset.dispatchDate / dispatchKind / dispatchCountdown /
 *   dispatchMinutes / dispatchDay and a bubbling `stock-block:dispatch` event). Where the ship
 *   line runs, it shows dispatch and delivery in its own row and this row is
 *   hidden; everywhere else the row is unchanged.
 *
 * v7.3 Changes:
 * ✓ Launch window: config.launchWindow (free text like "late August") hides
 *   the block for unavailable variants, same as the launch date. The buy
 *   button carries the window label and the notify app carries the CTA.
 *   Wins over config.launchDate.
 *
 * v7.2 Changes:
 * ✓ Launch state: config.launchDate in the future hides the whole block for
 *   unavailable variants. The buy button carries the launch date; the block
 *   reappears once stock is set or the date passes.
 *
 * v7.1 Changes:
 * ✓ Reads config from JSON instead of data-attributes
 * ✓ Cleaner, more maintainable code structure
 * ✓ Fixed out-of-stock and custom-build shipping display
 * 
 * v7.0 Features:
 * ✓ Full i18n support - uses Shopify locale (not browser)
 * ✓ Busy Period Mode - global toggle with custom text
 * ✓ Made-to-Order with Production Lead Time
 * ✓ Locale-aware date formatting
 */
(function() {
  'use strict';

  // ─────────────────────────────────────────────────────────────────
  // Locale Utilities
  // ─────────────────────────────────────────────────────────────────
  
  function getLocaleConfig(shopLocale) {
    const localeMap = {
      'de': 'de-DE', 'en': 'en-US', 'fr': 'fr-FR', 'es': 'es-ES',
      'it': 'it-IT', 'nl': 'nl-NL', 'pl': 'pl-PL', 'pt': 'pt-PT',
      'da': 'da-DK', 'sv': 'sv-SE', 'no': 'nb-NO', 'fi': 'fi-FI'
    };
    
    const locale = localeMap[shopLocale] || localeMap[shopLocale?.split('-')[0]] || 'en-US';
    
    return {
      locale,
      dateStyle: ['de', 'nl', 'pl'].includes(shopLocale) ? 'de' : 'en'
    };
  }
  
  function getShortDay(dayIndex, locale) {
    const d = new Date(2024, 0, 7 + dayIndex);
    return d.toLocaleDateString(locale, { weekday: 'short' }).replace('.', '');
  }
  
  function getLongDay(dayIndex, locale) {
    const d = new Date(2024, 0, 7 + dayIndex);
    return d.toLocaleDateString(locale, { weekday: 'long' });
  }

  // A warehouse date ('YYYY-MM-DD'), shown as written: read and formatted in UTC.
  function formatDateShort(iso, locale, dateStyle) {
    const date = isoDay(iso);
    const day = date.getUTCDate().toString().padStart(2, '0');
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const weekday = getShortDay(date.getUTCDay(), locale);

    if (dateStyle === 'de') {
      return `${weekday}, ${day}.${month}.`;
    } else {
      const monthName = date.toLocaleDateString(locale, { month: 'short', timeZone: 'UTC' });
      return `${weekday}, ${monthName} ${parseInt(day)}`;
    }
  }

  // A restock date in the past means the field went stale (restocked, sold
  // out again, no new date entered). Stale dates must never be shown.
  // Compared as warehouse dates: the date itself is still usable that day.
  function isRestockDateUsable(dateStr, todayIso) {
    if (!dateStr) return false;
    const day = String(dateStr).slice(0, 10);
    if (!ISO_DAY.test(day) || isNaN(isoDay(day).getTime())) return false;
    return day >= todayIso;
  }

  // Date-only values are calendar dates, not instants: no zone shift.
  function formatRestockDate(dateStr, locale) {
    const day = String(dateStr).slice(0, 10);
    if (ISO_DAY.test(day)) {
      return isoDay(day).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
    }
    return new Date(dateStr).toLocaleDateString(locale, { weekday: 'short', day: 'numeric', month: 'short' });
  }

  // Escape user/locale string for safe use inside a RegExp constructor.
  function escapeRegExp(str) {
    return String(str || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  // ─────────────────────────────────────────────────────────────────
  // Cutoff Time Parser
  // ─────────────────────────────────────────────────────────────────
  
  function parseCutoffHour(value, fallback = 10.5) {
    if (value === null || value === undefined || value === '') return fallback;
    
    const str = String(value).trim();
    
    const timeMatch = str.match(/^(\d{1,2}):(\d{2})$/);
    if (timeMatch) {
      const hours = parseInt(timeMatch[1], 10);
      const mins = parseInt(timeMatch[2], 10);
      if (hours >= 0 && hours <= 23 && mins >= 0 && mins <= 59) {
        return hours + mins / 60;
      }
    }
    
    const decimal = parseFloat(str);
    if (!isNaN(decimal) && decimal >= 0 && decimal <= 24) {
      return decimal;
    }
    
    console.warn('⚠️ Stock Block: Invalid cutoff "' + value + '", using default ' + fallback);
    return fallback;
  }

  // ─────────────────────────────────────────────────────────────────
  // Holiday Parser
  // ─────────────────────────────────────────────────────────────────
  
  function parseHolidays(str) {
    if (!str) return [];
    
    let normalized = str
      .replace(/&amp;#10;/g, ',')
      .replace(/&#10;/g, ',')
      .replace(/&amp;#13;/g, '')
      .replace(/&#13;/g, '')
      .replace(/\r?\n/g, ',')
      .replace(/[\s]+/g, ',');
    
    return normalized
      .split(',')
      .map(h => h.trim())
      .filter(h => /^\d{4}-\d{2}-\d{2}$/.test(h));
  }

  // ─────────────────────────────────────────────────────────────────
  // Business Day Functions
  // ─────────────────────────────────────────────────────────────────
  
  // ─────────────────────────────────────────────────────────────────
  // Warehouse calendar (pure; tools/check_logic.mjs in v1.0 Ship Line runs
  // it in Node under four browser zones and requires identical answers)
  // ─────────────────────────────────────────────────────────────────

  const WAREHOUSE_TZ = 'Europe/Berlin';
  const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
  const pad2 = (n) => String(n).padStart(2, '0');

  function zoneOrDefault(tz) {
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: tz });
      return tz;
    } catch (e) {
      return WAREHOUSE_TZ;
    }
  }

  // Wall clock of an instant in a zone: { iso, hour, minute }.
  function wallClock(instant, tz) {
    const p = {};
    new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
    }).formatToParts(instant).forEach((x) => { p[x.type] = x.value; });
    return { iso: `${p.year}-${p.month}-${p.day}`, hour: Number(p.hour) % 24, minute: Number(p.minute) };
  }

  // Day math on UTC noon: no zone and no DST can move a date.
  function isoDay(iso) {
    return new Date(`${iso}T12:00:00Z`);
  }

  function isoOf(date) {
    return `${date.getUTCFullYear()}-${pad2(date.getUTCMonth() + 1)}-${pad2(date.getUTCDate())}`;
  }

  function addDays(iso, n) {
    const d = isoDay(iso);
    d.setUTCDate(d.getUTCDate() + n);
    return isoOf(d);
  }

  function weekdayOf(iso) {
    return isoDay(iso).getUTCDay();
  }

  function isHoliday(iso, holidays) {
    return holidays.includes(iso);
  }

  function isBusinessDay(iso, holidays) {
    const day = weekdayOf(iso);
    return day !== 0 && day !== 6 && !isHoliday(iso, holidays);
  }

  function getNextBusinessDay(fromIso, skipToday, holidays) {
    let iso = skipToday ? addDays(fromIso, 1) : fromIso;
    let safety = 30;
    while (!isBusinessDay(iso, holidays) && safety-- > 0) iso = addDays(iso, 1);
    return iso;
  }

  function addBusinessDays(fromIso, days, holidays) {
    let iso = fromIso;
    let added = 0, safety = 100;
    while (added < days && safety-- > 0) {
      iso = addDays(iso, 1);
      if (isBusinessDay(iso, holidays)) added++;
    }
    return iso;
  }

  // The instant of a warehouse wall-clock time on a warehouse date (DST-safe:
  // the zone offset is read back from Intl until it settles).
  function wallInstant(iso, hours, tz) {
    const [y, m, d] = iso.split('-').map(Number);
    const h = Math.floor(hours);
    const min = Math.round((hours - h) * 60);
    const target = Date.UTC(y, m - 1, d, h, min);
    let guess = target;
    for (let i = 0; i < 4; i++) {
      const w = wallClock(new Date(guess), tz);
      const [wy, wm, wd] = w.iso.split('-').map(Number);
      const diff = Date.UTC(wy, wm - 1, wd, w.hour, w.minute) - target;
      if (!diff) break;
      guess -= diff;
    }
    return guess;
  }

  // When an order placed at this instant leaves the warehouse:
  //   { kind: 'today', date, minutes }  before the cutoff on a ship day
  //   { kind: 'tomorrow', date }        after the cutoff, next ship day is tomorrow
  //   { kind: 'day', date }             otherwise (weekend, holiday, later day)
  // minutes: whole minutes to the cutoff, counted from the current minute.
  function dispatchPlan(instant, tz, cutoffHour, holidays) {
    const now = wallClock(instant, tz);
    const today = now.iso;
    if (!isBusinessDay(today, holidays)) {
      return { kind: 'day', date: getNextBusinessDay(today, true, holidays) };
    }
    const minuteStart = Math.floor(instant.getTime() / 60000) * 60000;
    const minutes = Math.round((wallInstant(today, cutoffHour, tz) - minuteStart) / 60000);
    if (minutes > 0) return { kind: 'today', date: today, minutes };
    const date = getNextBusinessDay(today, true, holidays);
    return { kind: date === addDays(today, 1) ? 'tomorrow' : 'day', date };
  }

  // A launch date as a warehouse calendar date. Liquid hands the Date
  // metafield over as "YYYY-MM-DDT00:00:00"; a bare date or a zone-less
  // midnight is a calendar date, anything else (a real timestamp) is not.
  function launchDay(value) {
    const m = /^(\d{4}-\d{2}-\d{2})(?:T00:00(?::00(?:\.0+)?)?)?$/.exec(String(value || ''));
    return m ? m[1] : null;
  }

  // Launch state pending at this instant: before the launch day on the
  // warehouse calendar (or before a real timestamp).
  function launchPendingAt(value, instant, tz) {
    if (!value) return false;
    const day = launchDay(value);
    if (day) return day > wallClock(instant, tz).iso;
    const d = new Date(value);
    return !isNaN(d.getTime()) && d > instant;
  }

  // "9. Oktober": the launch day as written, no zone shift.
  function formatLaunchDate(value, locale) {
    const day = launchDay(value);
    if (day) return isoDay(day).toLocaleDateString(locale, { day: 'numeric', month: 'long', timeZone: 'UTC' });
    return new Date(value).toLocaleDateString(locale, { day: 'numeric', month: 'long' });
  }

  // stockblock_time: with Z or an offset an instant, without one a warehouse
  // wall-clock time (the same simulated moment in every browser).
  function parseSimulatedTime(value, tz) {
    const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(String(value || ''));
    if (m) return new Date(wallInstant(m[1], Number(m[2]) + Number(m[3]) / 60, tz) + Number(m[4] || 0) * 1000);
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }

  if (typeof module === 'object' && module.exports) {
    module.exports = {
      wallClock, wallInstant, dispatchPlan, addBusinessDays, getNextBusinessDay, parseSimulatedTime,
      isRestockDateUsable, formatRestockDate, formatDateShort, parseHolidays, parseCutoffHour, zoneOrDefault,
      launchDay, launchPendingAt, formatLaunchDate
    };
    return;
  }

  // ─────────────────────────────────────────────────────────────────
  // Main Block Initialization
  // ─────────────────────────────────────────────────────────────────
  
  function initStockBlock(block) {
    // Parse JSON config
    const configEl = block.querySelector('[data-stock-config]');
    if (!configEl) {
      console.error('Stock Block: Missing config');
      return;
    }
    
    let rawConfig;
    try {
      rawConfig = JSON.parse(configEl.textContent);
    } catch (e) {
      console.error('Stock Block: Invalid config JSON', e);
      return;
    }

    // Get display settings from data attributes (needed for CSS)
    const style = block.dataset.style;
    
    // Build config from JSON
    const localeConfig = getLocaleConfig(rawConfig.locale);
    const holidays = parseHolidays(rawConfig.shipping.holidays || '');
    const cutoffHour = parseCutoffHour(rawConfig.shipping.cutoffHour, 10.5);
    
    if (holidays.length > 0) {
      console.log('📅 Stock Block: Holidays →', holidays);
    }
    
    // Warehouse zone for every calendar decision.
    const warehouseTz = zoneOrDefault(rawConfig.timezone || WAREHOUSE_TZ);

    // Time simulation for testing
    const urlParams = new URLSearchParams(window.location.search);
    const debugTime = urlParams.get('stockblock_time') || null;

    const getSimulatedNow = () => {
      if (debugTime) {
        const sim = parseSimulatedTime(debugTime, warehouseTz);
        if (sim) return sim;
      }
      return new Date();
    };
    const warehouseToday = () => wallClock(getSimulatedNow(), warehouseTz).iso;
    
    if (debugTime) console.log('🕐 Stock Block: Time simulation →', debugTime);

    const config = {
      timezone: warehouseTz,
      timezoneLabel: rawConfig.timezoneLabel,
      lowThreshold: rawConfig.thresholds.low,
      mediumThreshold: rawConfig.thresholds.medium,
      urgencyThreshold: rawConfig.thresholds.urgency,
      showQuantity: rawConfig.display.showQuantity,
      showCountdown: rawConfig.display.showCountdown,
      showDelivery: rawConfig.display.showDelivery,
      cutoffHour,
      deliveryDays: rawConfig.shipping.deliveryDays,
      busyMode: rawConfig.busyMode,
      localeConfig,
      texts: rawConfig.texts,
      boldMarkers: rawConfig.boldMarkers || { sameDay: 'same-day shipping', tomorrow: 'tomorrow' },
      variants: rawConfig.variants,
      launchDate: rawConfig.launchDate || null,
      launchWindow: rawConfig.launchWindow || null
    };

    const els = {
      dot: block.querySelector('[data-stock-dot]'),
      icon: block.querySelector('[data-stock-icon]'),
      iconCheck: block.querySelector('[data-icon-check]'),
      iconWarning: block.querySelector('[data-icon-warning]'),
      iconX: block.querySelector('[data-icon-x]'),
      iconClock: block.querySelector('[data-icon-clock]'),
      badge: block.querySelector('[data-stock-badge]'),
      badgeText: block.querySelector('[data-badge-text]'),
      text: block.querySelector('[data-stock-text]'),
      restockEta: block.querySelector('[data-restock-eta]'),
      restockText: block.querySelector('[data-restock-text]'),
      leadTimeEta: block.querySelector('[data-lead-time-eta]'),
      leadTimeText: block.querySelector('[data-lead-time-text]'),
      shippingContainer: block.querySelector('[data-shipping-container]'),
      shippingInfo: block.querySelector('[data-shipping-info]'),
      deliveryEstimate: block.querySelector('[data-delivery-estimate]'),
      deliveryText: block.querySelector('[data-delivery-text]')
    };

    const css = getComputedStyle(block);
    const colors = {
      high: css.getPropertyValue('--stock-color-high').trim(),
      highBg: css.getPropertyValue('--stock-color-high-bg').trim(),
      medium: css.getPropertyValue('--stock-color-medium').trim(),
      mediumBg: css.getPropertyValue('--stock-color-medium-bg').trim(),
      low: css.getPropertyValue('--stock-color-low').trim(),
      lowBg: css.getPropertyValue('--stock-color-low-bg').trim(),
      out: css.getPropertyValue('--stock-color-out').trim(),
      outBg: css.getPropertyValue('--stock-color-out-bg').trim(),
      madeToOrder: css.getPropertyValue('--stock-color-made-to-order').trim(),
      madeToOrderBg: css.getPropertyValue('--stock-color-made-to-order-bg').trim(),
      leadTime: css.getPropertyValue('--stock-color-lead-time').trim(),
      preOrder: css.getPropertyValue('--stock-color-pre-order').trim(),
      preOrderBg: css.getPropertyValue('--stock-color-pre-order-bg').trim()
    };

    function getVariantData(variantId) {
      return config.variants.find(v => v.id === variantId) || config.variants[0];
    }

    // Launch state: the product has a launch date that has not been reached
    // yet. Only ever replaces an otherwise-unavailable state; once the date
    // passes, every path falls back to the normal out-of-stock behavior.
    function isLaunchPending() {
      // A launch date is a warehouse date: pending until that day (v7.6).
      return launchPendingAt(config.launchDate, getSimulatedNow(), warehouseTz);
    }

    function launchStatus() {
      return { status: 'launch', color: colors.out, bg: colors.outBg };
    }

    // Launch window: month-granularity launch statement ("late August").
    // Behaves like the exact-date launch state: the block hides, the buy
    // button carries the window label, the notify app button is the CTA.
    function launchWindowStatus() {
      return { status: 'launch_window', color: colors.out, bg: colors.outBg };
    }

    function getStockStatus(variant) {
      const hasLeadTime = variant.lt && variant.lt.trim() !== '';

      // Pre-order. One metafield drives both launch states, and availability
      // decides which one applies: with nothing on the shelf it stays the
      // "not yet, get notified" state further down, and with real stock
      // booked in it becomes a pre-order that says when the batch leaves.
      // The metafield is the switch, never the inventory policy on its own:
      // every made-to-order product also sells past zero and must keep its
      // own state. Clearing the metafield ends all of it.
      if (config.launchWindow) {
        const purchasable = variant.c || !variant.t || variant.q > 0;
        if (purchasable) {
          return { status: 'pre_order', color: colors.preOrder, bg: colors.preOrderBg };
        }
      }

      // Custom build with lead time (e.g., BNF drones)
      if (hasLeadTime) {
        // Out of stock custom build (no parts)
        if (variant.t && variant.q <= 0 && variant.c !== 1) {
          if (config.launchWindow) return launchWindowStatus();
          if (isLaunchPending()) return launchStatus();
          return { status: 'out', color: colors.out, bg: colors.outBg };
        }
        // Available custom build
        return { 
          status: 'custom_build', 
          color: colors.madeToOrder, 
          bg: colors.madeToOrderBg,
          leadTime: variant.lt
        };
      }
      
      // Continue selling = always available
      if (variant.c) {
        return { status: 'made_to_order', color: colors.madeToOrder, bg: colors.madeToOrderBg };
      }
      
      // Not tracking inventory = always available
      if (!variant.t) {
        return { status: 'made_to_order', color: colors.madeToOrder, bg: colors.madeToOrderBg };
      }
      
      // Out of stock
      if (variant.q <= 0) {
        if (config.launchWindow) return launchWindowStatus();
        if (isLaunchPending()) return launchStatus();
        return { status: 'out', color: colors.out, bg: colors.outBg, restockDate: variant.r };
      }
      
      // Low stock
      if (variant.q <= config.lowThreshold) {
        return { status: 'low', color: colors.low, bg: colors.lowBg, qty: variant.q };
      }
      
      // Medium stock
      if (variant.q <= config.mediumThreshold) {
        return { status: 'medium', color: colors.medium, bg: colors.mediumBg, qty: variant.q };
      }
      
      // High stock
      return { status: 'high', color: colors.high, bg: colors.highBg, qty: variant.q };
    }

    function getStockText(stock) {
      switch (stock.status) {
        case 'made_to_order': 
          return config.texts.madeToOrder;
        case 'custom_build': 
          return config.texts.customBuild;
        case 'high': 
          return config.showQuantity 
            ? config.texts.highStockCount.replace('[count]', config.mediumThreshold) 
            : config.texts.inStock;
        case 'medium': 
          return config.showQuantity 
            ? config.texts.mediumStockCount.replace('[count]', stock.qty) 
            : config.texts.inStock;
        case 'low': 
          return config.showQuantity 
            ? config.texts.lowStockCount.replace('[count]', stock.qty) 
            : config.texts.lowStock;
        case 'out':
          return config.texts.outOfStock;
        case 'launch': {
          if (!config.texts.launch) return config.texts.outOfStock;
          // Day + long month, no weekday: matches the Liquid-rendered launch
          // date on the buy button and the card badges.
          const launchDateText = formatLaunchDate(config.launchDate, config.localeConfig.locale);
          return config.texts.launch.replace('[date]', launchDateText);
        }
        case 'launch_window':
          return config.texts.outOfStock;
        case 'pre_order':
          return config.texts.preOrder || config.texts.outOfStock;
        default:
          return config.texts.inStock;
      }
    }

    function updateStock(variantId, animate = false) {
      const variant = getVariantData(variantId);
      const stock = getStockStatus(variant);
      const text = getStockText(stock);
      const isLow = stock.status === 'low';
      const isUrgent = isLow && stock.qty <= config.urgencyThreshold;
      const isMadeToOrder = stock.status === 'made_to_order';
      const isCustomBuild = stock.status === 'custom_build';
      const isLaunch = stock.status === 'launch';
      const isLaunchWindow = stock.status === 'launch_window';
      const isPreOrder = stock.status === 'pre_order';

      // Launch state (date or window): the whole block stays hidden. The buy
      // button carries the launch statement; repeating it here reads as
      // clutter. The block reappears automatically once stock is set, the
      // date passes or the window metafield is cleared.
      block.style.display = isLaunch || isLaunchWindow ? 'none' : '';
      if (isLaunch || isLaunchWindow) return;

      // Update dot
      if (els.dot) {
        els.dot.style.backgroundColor = stock.color;
        els.dot.style.display = style === 'dot' || style === 'outline' ? '' : 'none';
        els.dot.classList.toggle('stock-dot--outline', style === 'outline');
        els.dot.classList.toggle('stock-dot--pulse', isLow && block.dataset.pulse !== 'none');
      }

      // Update icon
      if (els.icon) {
        els.icon.style.color = stock.color;
        els.icon.style.display = style === 'icon' ? '' : 'none';
        if (els.iconCheck) els.iconCheck.style.display = ['high', 'medium'].includes(stock.status) ? '' : 'none';
        if (els.iconWarning) els.iconWarning.style.display = stock.status === 'low' ? '' : 'none';
        if (els.iconX) els.iconX.style.display = stock.status === 'out' ? '' : 'none';
        if (els.iconClock) els.iconClock.style.display = (isMadeToOrder || isCustomBuild || isLaunch || isPreOrder) ? '' : 'none';
      }

      // Update badge
      if (els.badge) {
        els.badge.style.display = style === 'badge' ? '' : 'none';
        els.badge.style.backgroundColor = stock.bg;
        els.badge.style.color = stock.color;
        if (els.badgeText) els.badgeText.textContent = text;
      }

      // Update text
      if (els.text) {
        els.text.textContent = text;
        els.text.style.color = stock.color;
        els.text.classList.toggle('stock-text--urgent', isUrgent);
        if (animate && isUrgent) {
          els.text.classList.remove('stock-text--shake');
          void els.text.offsetWidth;
          els.text.classList.add('stock-text--shake');
        }
      }

      // Update restock ETA (for out of stock items)
      if (els.restockEta) {
        if (stock.status === 'out' && isRestockDateUsable(variant.r, warehouseToday())) {
          const formattedDate = formatRestockDate(variant.r, config.localeConfig.locale);
          if (els.restockText) {
            els.restockText.textContent = config.texts.restockEta.replace('[date]', formattedDate);
          }
          els.restockEta.style.display = '';
        } else {
          els.restockEta.style.display = 'none';
        }
      }

      // Update lead time ETA (for custom builds)
      if (els.leadTimeEta) {
        if (isCustomBuild && stock.leadTime) {
          if (els.leadTimeText) {
            els.leadTimeText.textContent = config.texts.leadTime.replace('[time]', stock.leadTime);
          }
          els.leadTimeEta.style.display = '';
        } else {
          els.leadTimeEta.style.display = 'none';
        }
      }

      // Update shipping container visibility (handled in updateShipping)
      block.classList.remove('custom-stock-block--loading');
    }

    // v1.0 Ship Line: the dispatch day this row states (warehouse date,
    // YYYY-MM-DD) and how: kind today (with the countdown), tomorrow or day
    // (with the weekday name). Empty whenever the row states no concrete day:
    // busy mode, custom build, pre-order, sold out, launch. The ship line
    // listens on the block; the event fires again when the countdown changes.
    let dispatchInfo = null;
    function publishDispatch(date) {
      const iso = date && ISO_DAY.test(date) ? date : '';
      const info = iso && dispatchInfo ? dispatchInfo : { kind: '' };
      const values = {
        dispatchDate: iso,
        dispatchKind: info.kind || '',
        dispatchCountdown: info.countdown || '',
        dispatchDay: info.day || '',
        dispatchMinutes: info.minutes ? String(info.minutes) : '',
      };
      if (Object.entries(values).every(([k, v]) => (block.dataset[k] || '') === v)) return;
      Object.entries(values).forEach(([k, v]) => {
        if (v) block.dataset[k] = v;
        else delete block.dataset[k];
      });
      block.dispatchEvent(new CustomEvent('stock-block:dispatch', { bubbles: true, detail: values }));
    }

    function updateShipping() {
      if (!els.shippingInfo) return;
      dispatchInfo = null;
      publishDispatch(computeShipping() || null);
    }

    // Writes the dispatch row; returns the dispatch day it states, if any.
    function computeShipping() {
      const variant = getVariantData(currentVariantId);
      const stock = getStockStatus(variant);
      const isMadeToOrder = stock.status === 'made_to_order';
      const isCustomBuild = stock.status === 'custom_build';
      const isOutOfStock = stock.status === 'out';
      const isLaunch = stock.status === 'launch';
      const isLaunchWindow = stock.status === 'launch_window';
      const isPreOrder = stock.status === 'pre_order';

      // Pre-order: the shipping row states when the batch leaves, and the
      // delivery estimate is suppressed. Nothing in this state may imply a
      // date on the doormat. The window text comes from the metafield, so it
      // is one Admin edit away and translated once.
      if (isPreOrder) {
        if (config.launchWindow && config.texts.preOrderShips) {
          els.shippingInfo.textContent = config.texts.preOrderShips.replace('[window]', config.launchWindow);
          if (els.shippingContainer) els.shippingContainer.style.display = '';
        } else if (els.shippingContainer) {
          els.shippingContainer.style.display = 'none';
        }
        if (els.deliveryEstimate) els.deliveryEstimate.style.display = 'none';
        return;
      }

      // Out of stock and pre-launch: Hide shipping completely
      if (isOutOfStock || isLaunch || isLaunchWindow) {
        if (els.shippingContainer) {
          els.shippingContainer.style.display = 'none';
        }
        return;
      }

      // Custom builds: Hide regular shipping (lead time row handles it)
      if (isCustomBuild) {
        if (config.busyMode.customBuildEnabled) {
          const busyText = config.busyMode.customBuildText || config.texts.busyDefault;
          els.shippingInfo.innerHTML = busyText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
          if (els.shippingContainer) els.shippingContainer.style.display = '';
        } else {
          if (els.shippingContainer) {
            els.shippingContainer.style.display = 'none';
          }
        }
        if (els.deliveryEstimate) {
          els.deliveryEstimate.style.display = 'none';
        }
        return;
      }

      // Check busy mode for other product types
      let busyText = '';
      if (isMadeToOrder && config.busyMode.madeToOrderEnabled) {
        busyText = config.busyMode.madeToOrderText || config.texts.busyDefault;
      } else if (config.busyMode.enabled && !isMadeToOrder) {
        busyText = config.busyMode.text || config.texts.busyDefault;
      }

      if (busyText) {
        const html = busyText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        els.shippingInfo.innerHTML = html;
        if (els.shippingContainer) els.shippingContainer.style.display = '';
        if (els.deliveryEstimate) els.deliveryEstimate.style.display = 'none';
        return;
      }
      
      // Show shipping container for regular products
      if (els.shippingContainer) {
        els.shippingContainer.style.display = '';
      }

      // Warehouse calendar only (v7.5): the plan never depends on the
      // browser's zone. shipsDate is a warehouse date, 'YYYY-MM-DD'.
      const now = getSimulatedNow();
      const plan = dispatchPlan(now, config.timezone, config.cutoffHour, holidays);
      const canShipToday = isBusinessDay(wallClock(now, config.timezone).iso, holidays);

      let shipsDate, html = '';
      const { locale, dateStyle } = config.localeConfig;

      const formatCountdown = (minutesLeft) => {
        if (!(minutesLeft > 0)) return null;
        const hrs = Math.floor(minutesLeft / 60), mins = minutesLeft % 60;
        if (hrs > 0 && mins > 0) return `${hrs}h ${mins}m`;
        return hrs > 0 ? `${hrs}h` : `${mins}m`;
      };

      if (!canShipToday) {
        shipsDate = plan.date;
        const dayName = getLongDay(weekdayOf(shipsDate), locale);
        dispatchInfo = { kind: 'day', day: dayName };
        html = config.texts.shipsNextDay
          .replace('[day]', dayName)
          .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        if (!config.texts.shipsNextDay.includes('**')) {
          html = config.texts.shipsNextDay.replace('[day]', `<strong>${dayName}</strong>`);
        }
      } else {
        const countdown = plan.kind === 'today' ? formatCountdown(plan.minutes) : null;
        if (countdown) {
          shipsDate = plan.date;
          // Whole minutes to the cutoff, the same count as the countdown text.
          dispatchInfo = { kind: 'today', countdown, minutes: plan.minutes };
          html = config.texts.sameDay
            .replace('[time]', `<span class="shipping-countdown">${countdown}</span>`)
            .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
          if (!config.texts.sameDay.includes('**')) {
            const marker = config.boldMarkers.sameDay;
            if (marker) {
              const re = new RegExp(escapeRegExp(marker), 'gi');
              html = html.replace(re, `<strong>${marker}</strong>`);
            }
          }
        } else {
          shipsDate = plan.date;
          const dayName = getLongDay(weekdayOf(shipsDate), locale);

          if (plan.kind === 'tomorrow') {
            dispatchInfo = { kind: 'tomorrow' };
            html = config.texts.shipsTomorrow
              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
            if (!config.texts.shipsTomorrow.includes('**')) {
              const marker = config.boldMarkers.tomorrow;
              if (marker) {
                const re = new RegExp('\\b' + escapeRegExp(marker) + '\\b', 'gi');
                html = config.texts.shipsTomorrow.replace(re, `<strong>${marker}</strong>`);
              }
            }
          } else {
            dispatchInfo = { kind: 'day', day: dayName };
            html = config.texts.shipsNextDay
              .replace('[day]', dayName)
              .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
            if (!config.texts.shipsNextDay.includes('**')) {
              html = config.texts.shipsNextDay.replace('[day]', `<strong>${dayName}</strong>`);
            }
          }
        }
      }

      els.shippingInfo.innerHTML = html;

      // Update delivery estimate
      if (els.deliveryEstimate && els.deliveryText && shipsDate && config.showDelivery) {
        const dd = addBusinessDays(shipsDate, config.deliveryDays, holidays);
        const formattedDate = formatDateShort(dd, locale, dateStyle);
        els.deliveryText.innerHTML = `${config.texts.delivery} <span class="delivery-date">${formattedDate}</span>`;
        els.deliveryEstimate.style.display = '';
      }
      return shipsDate;
    }

    // ─────────────────────────────────────────────────────────────────
    // Variant Detection
    // ─────────────────────────────────────────────────────────────────
    
    let currentVariantId = rawConfig.currentVariantId || 0;

    function onVariantChange(id) {
      if (!id || id === currentVariantId) return;
      currentVariantId = id;
      block.classList.add('custom-stock-block--updating');
      requestAnimationFrame(() => {
        updateStock(id, true);
        updateShipping();
        block.classList.remove('custom-stock-block--updating');
      });
    }

    // 1. Native Shopify/Theme Events
    ['variant:changed', 'variant:change', 'variantChange', 'theme:variant:change'].forEach(evt => {
      document.addEventListener(evt, (e) => {
        const id = e.detail?.variant?.id || e.detail?.id;
        if (id) onVariantChange(parseInt(id));
      });
    });

    // 2. MutationObserver on variant input
    const form = block.closest('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]');
    if (form) {
      const input = form.querySelector('input[name="id"]');
      if (input) {
        new MutationObserver(() => {
          const id = parseInt(input.value);
          if (id) onVariantChange(id);
        }).observe(input, { attributes: true, attributeFilter: ['value'] });
        
        let lastVal = input.value;
        form.addEventListener('change', () => {
          setTimeout(() => {
            if (input.value !== lastVal) {
              lastVal = input.value;
              onVariantChange(parseInt(input.value));
            }
          }, 10);
        });
      }
    }

    // 3. URL popstate
    const checkUrl = () => {
      const v = new URLSearchParams(window.location.search).get('variant');
      if (v) onVariantChange(parseInt(v));
    };
    window.addEventListener('popstate', checkUrl);
    
    // 4. URL polling
    let lastHref = window.location.href;
    const poll = setInterval(() => {
      if (window.location.href !== lastHref) {
        lastHref = window.location.href;
        checkUrl();
      }
    }, 200);

    // ─────────────────────────────────────────────────────────────────
    // Init
    // ─────────────────────────────────────────────────────────────────
    
    updateStock(currentVariantId);
    if (config.showCountdown) {
      updateShipping();
      block._shippingInterval = setInterval(updateShipping, 60000);
    }

    block._cleanup = () => {
      clearInterval(poll);
      if (block._shippingInterval) clearInterval(block._shippingInterval);
    };

    requestAnimationFrame(() => block.classList.remove('custom-stock-block--loading'));
  }

  function init() {
    document.querySelectorAll('[data-stock-block]').forEach(initStockBlock);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
